export interface LogoItem {
  id: string;
  src: string;
  alt?: string;
}

export interface LogoStrip {
  /** Unique strip id so you can use it as a React key */
  id: number;
  /** Logos rendered inside this strip (usually the same base sequence) */
  logos: LogoItem[];
  /** Left offset in pixels */
  x: number;
}

/**
 * Manages a set of horizontal logo strips that scroll endlessly from right to left.
 *
 * - You give it a base list of logos and a fixed strip width in pixels
 * - It creates enough strips to cover (viewportWidth + stripWidth)
 * - On each update, it moves all strips left
 * - When a strip has fully left the screen, it is removed and a new strip is
 *   appended on the right ("destroyed" and regenerated)
 */
export class InfiniteLogoStrips {
  private baseLogos: LogoItem[];
  private stripWidth: number;
  private viewportWidth: number;
  private speedPxPerSecond: number;

  private nextStripId = 0;
  private stripsInternal: LogoStrip[] = [];

  constructor(options: {
    baseLogos: LogoItem[];
    /** Width of one strip in pixels (should roughly match your rendered CSS width) */
    stripWidth: number;
    /** Visible width of the viewport / container in pixels */
    viewportWidth: number;
    /** Scroll speed in px / second (positive number, moves left) */
    speedPxPerSecond?: number;
  }) {
    const { baseLogos, stripWidth, viewportWidth, speedPxPerSecond = 40 } = options;

    this.baseLogos = baseLogos;
    this.stripWidth = stripWidth;
    this.viewportWidth = viewportWidth;
    this.speedPxPerSecond = speedPxPerSecond;

    this.initStrips();
  }

  /** Current live strips (read-only view) */
  get strips(): LogoStrip[] {
    return this.stripsInternal;
  }

  /**
   * Advance the animation by a time delta (in milliseconds).
   * Call this from requestAnimationFrame or an interval.
   */
  update(deltaMs: number): void {
    const deltaPx = (this.speedPxPerSecond * deltaMs) / 1000;

    // Move all strips left
    this.stripsInternal = this.stripsInternal.map((strip) => ({
      ...strip,
      x: strip.x - deltaPx,
    }));

    // Remove strips that are completely off-screen to the left
    const stillVisible: LogoStrip[] = [];
    for (const strip of this.stripsInternal) {
      if (strip.x + this.stripWidth > 0) {
        stillVisible.push(strip);
      }
    }
    this.stripsInternal = stillVisible;

    // Ensure we have enough strips to cover viewport + one extra strip
    this.ensureCoverage();
  }

  /** Reset back to the initial state */
  reset(): void {
    this.nextStripId = 0;
    this.stripsInternal = [];
    this.initStrips();
  }

  private initStrips(): void {
    this.stripsInternal = [];

    // Start with the first strip at x = 0
    let currentX = 0;
    while (currentX < this.viewportWidth + this.stripWidth) {
      this.stripsInternal.push(this.createStripAt(currentX));
      currentX += this.stripWidth;
    }
  }

  private ensureCoverage(): void {
    if (this.stripsInternal.length === 0) {
      // Nothing left; rebuild
      this.initStrips();
      return;
    }

    let maxX = Math.max(...this.stripsInternal.map((s) => s.x));

    // Add new strips on the right until we cover viewport + one extra strip
    while (maxX + this.stripWidth < this.viewportWidth + this.stripWidth) {
      const newX = maxX + this.stripWidth;
      const newStrip = this.createStripAt(newX);
      this.stripsInternal.push(newStrip);
      maxX = newX;
    }
  }

  private createStripAt(x: number): LogoStrip {
    const strip: LogoStrip = {
      id: this.nextStripId++,
      logos: this.baseLogos.slice(),
      x,
    };
    return strip;
  }
}
